/**
 * The pricing API sends dollars as JSON numbers (`7.35`, `35.00`). Every
 * user-facing money string goes through here so amounts never drift between
 * surfaces (`$7.4` vs `$7.35`).
 */

const DEFAULT_CURRENCY = 'usd';

export function formatUsd(amount: number, currency: string = DEFAULT_CURRENCY): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currency.toUpperCase(),
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
}

/** Anything after this is milliseconds; anything before it is seconds. ~Sep 2033 as ms. */
const SECONDS_CUTOFF = 2_000_000_000;

/**
 * Reset dates and extra-credits effective dates, e.g. "Oct 19, 2026".
 *
 * Accepts an ISO string or a Unix timestamp. The payment API sends timestamps in
 * SECONDS, which `new Date()` reads as milliseconds — silently yielding a 1970
 * date rather than throwing — so numeric input is scaled explicitly.
 */
export function formatEffectiveDate(
	isoDate: string | number,
	{ includeYear = true }: { includeYear?: boolean } = {}
): string {
	const date =
		typeof isoDate === 'number' || /^\d+$/.test(isoDate)
			? new Date(Number(isoDate) * (Number(isoDate) < SECONDS_CUTOFF ? 1000 : 1))
			: new Date(isoDate);

	if (Number.isNaN(date.getTime())) {
		return '';
	}

	return new Intl.DateTimeFormat('en-US', {
		day: 'numeric',
		month: 'short',
		year: includeYear ? 'numeric' : undefined,
	}).format(date);
}

/** Per-action prices are sub-cent, so they need more precision than `formatUsd`. */
export function formatUnitPrice(amount: number): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 3,
	}).format(amount);
}

/** Whole-dollar amounts for limit options, e.g. "$25" rather than "$25.00". */
export function formatWholeUsd(amount: number, currency: string = DEFAULT_CURRENCY): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currency.toUpperCase(),
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(amount);
}
